import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Mail, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import {
  DELETE_ACCOUNT,
  SUPPORT_EMAIL,
  LAST_UPDATED_EN,
  LAST_UPDATED_AR,
} from '../data/legalContent';
import './LegalPages.css';

const API = process.env.REACT_APP_BACKEND_URL;

/** Public email-code deletion flow: email → code + confirm → done. */
function PublicDeletionForm({ isRTL }) {
  const [step, setStep] = React.useState('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [confirmed, setConfirmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const t = (en, ar) => (isRTL ? ar : en);

  const startRequest = async () => {
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError(t('Please enter a valid email address.', 'يرجى إدخال بريد إلكتروني صحيح.'));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/account/deletion-request/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (data && data.email_delivery === 'failed') {
        setError(t('We could not send the email. Please try again shortly.',
          'تعذّر إرسال البريد الإلكتروني. حاول مرة أخرى بعد قليل.'));
      } else {
        setStep('code');
      }
    } catch (e) {
      setError(t('Connection error. Please try again.', 'خطأ في الاتصال. حاول مرة أخرى.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeletion = async () => {
    setError('');
    if (!/^\d{6}$/.test(code.trim())) {
      setError(t('Enter the 6-digit code from your email.', 'أدخل الرمز المكوَّن من 6 أرقام من بريدك.'));
      return;
    }
    if (!confirmed) {
      setError(t('Please tick the confirmation box first.', 'يرجى تأكيد الموافقة أولاً.'));
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/account/deletion-request/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim() }),
      });
      if (res.ok) {
        setStep('done');
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = typeof data?.detail === 'string' ? data.detail : '';
        if (res.status === 429) {
          setError(t('Too many attempts. Please request a new code.',
            'محاولات كثيرة. اطلب رمزاً جديداً.'));
          setStep('email');
          setCode('');
        } else {
          setError(detail === 'Invalid or expired code'
            ? t('Invalid or expired code. Check the code or request a new one.',
                'رمز غير صحيح أو منتهي. تحقق من الرمز أو اطلب رمزاً جديداً.')
            : (detail || t('Something went wrong. Please try again.', 'حدث خطأ. حاول مرة أخرى.')));
        }
      }
    } catch (e) {
      setError(t('Connection error. Please try again.', 'خطأ في الاتصال. حاول مرة أخرى.'));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="legal-cta-card" data-testid="delete-success" style={{
        background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14,
        padding: 18, textAlign: 'center', marginTop: 10,
      }}>
        <CheckCircle2 size={34} color="#16A34A" style={{ marginBottom: 8 }} />
        <div style={{ fontWeight: 800, fontSize: 16, color: '#14532D' }} data-testid="delete-success-title">
          {t('Your account has been permanently deleted', 'تم حذف حسابك نهائياً')}
        </div>
        <p style={{ fontSize: 13, color: '#166534', margin: '8px 0 0' }}>
          {t('Your Wassla account, personal data and listings have been permanently removed. This cannot be undone.',
             'تم حذف حسابك في وصلة وبياناتك الشخصية وإعلاناتك نهائياً. لا يمكن التراجع عن ذلك.')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="public-deletion-form" style={{ marginTop: 8 }}>
      {step === 'email' && (
        <div>
          <input
            type="email"
            dir="ltr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('Your Wassla account email', 'بريدك الإلكتروني في وصلة')}
            data-testid="delete-email-input"
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              border: '1px solid #D1D5DB', fontSize: 15, marginBottom: 10,
            }}
          />
          <button
            type="button"
            className="legal-cta-primary"
            onClick={startRequest}
            disabled={busy}
            data-testid="delete-send-code-btn"
            style={{ width: '100%', opacity: busy ? 0.7 : 1 }}
          >
            {busy
              ? <Loader2 size={16} className="animate-spin" style={{ verticalAlign: 'middle' }} />
              : t('Send confirmation code', 'إرسال رمز التأكيد')}
          </button>
        </div>
      )}

      {step === 'code' && (
        <div>
          <p style={{ fontSize: 13, color: '#4B5563', marginBottom: 8 }} data-testid="delete-code-sent-note">
            {t(`If ${email.trim()} is registered, a 6-digit code was sent to it (valid 15 minutes).`,
               `إذا كان ${email.trim()} مسجلاً، فقد أُرسل إليه رمز من 6 أرقام (صالح 15 دقيقة).`)}
          </p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            dir="ltr"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            data-testid="delete-code-input"
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              border: '1px solid #D1D5DB', fontSize: 18, letterSpacing: 6,
              textAlign: 'center', marginBottom: 10,
            }}
          />
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#374151', marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="delete-confirm-checkbox"
              style={{ marginTop: 3 }}
            />
            <span>
              {t('I understand my Wassla account and associated personal data will be permanently deleted and this cannot be undone.',
                 'أفهم أن حسابي في وصلة وبياناتي الشخصية المرتبطة به ستُحذف نهائياً ولا يمكن التراجع عن ذلك.')}
            </span>
          </label>
          <button
            type="button"
            onClick={confirmDeletion}
            disabled={busy || !confirmed}
            data-testid="delete-confirm-btn"
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12, border: 'none',
              background: (busy || !confirmed) ? '#FCA5A5' : '#DC2626', color: '#fff',
              fontWeight: 800, fontSize: 15, cursor: (busy || !confirmed) ? 'default' : 'pointer',
            }}
          >
            {busy
              ? <Loader2 size={16} className="animate-spin" style={{ verticalAlign: 'middle' }} />
              : t('Permanently delete my account', 'حذف حسابي نهائياً')}
          </button>
          <button
            type="button"
            onClick={() => { setStep('email'); setCode(''); setConfirmed(false); setError(''); }}
            data-testid="delete-back-btn"
            style={{ width: '100%', marginTop: 8, padding: 10, borderRadius: 12, border: '1px solid #E5E7EB', background: '#fff', fontSize: 13, cursor: 'pointer' }}
          >
            {t('Use a different email / resend code', 'استخدام بريد آخر / إعادة إرسال الرمز')}
          </button>
        </div>
      )}

      {error && (
        <p data-testid="delete-error" style={{ color: '#DC2626', fontSize: 13, marginTop: 10, fontWeight: 600 }}>
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * iter306 — Public account-deletion instructions page (required by
 * Google Play / Apple App Store data-deletion policies).
 * No login required. EN/AR. Links to in-app deletion if the user is
 * signed in, otherwise gives the email fallback.
 */
export default function DeleteAccount() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const lang = isRTL ? 'ar' : 'en';
  const doc = DELETE_ACCOUNT[lang];
  const lastUpdated = isRTL ? LAST_UPDATED_AR : LAST_UPDATED_EN;

  return (
    <div className="legal-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="delete-account-page">
      <AppHeader title={doc.title} backFallback="/settings" testId="delete-account-header" />
      <div className="legal-container">
        <div className="legal-header">
          <div className="legal-icon-wrapper legal-icon-warn">
            <Trash2 size={32} />
          </div>
          <h1 className="legal-title" data-testid="delete-account-title">
            {doc.title}
          </h1>
          <p className="legal-subtitle" data-testid="delete-account-last-updated">
            {isRTL ? `آخر تحديث: ${lastUpdated}` : `Last updated: ${lastUpdated}`}
          </p>
          <p className="legal-intro">{doc.intro}</p>
        </div>

        <div className="legal-content" data-testid="delete-account-content">
          {/* In-app path */}
          <section className="legal-section" data-testid="delete-method-in-app">
            <h2>
              <ShieldCheck size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {doc.inAppTitle}
            </h2>
            <ol className="legal-ordered">
              {doc.inAppSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {user ? (
              <button
                type="button"
                className="legal-cta-primary"
                data-testid="delete-account-go-settings"
                onClick={() => navigate('/account?openDelete=1')}
              >
                {isRTL ? 'الانتقال إلى إعدادات الحساب' : 'Go to account settings'}
              </button>
            ) : (
              <Link
                to="/auth"
                className="legal-cta-primary"
                data-testid="delete-account-sign-in"
              >
                {isRTL ? 'تسجيل الدخول للمتابعة' : 'Sign in to continue'}
              </Link>
            )}
          </section>

          {/* Email fallback — interactive public deletion flow (no login) */}
          <section className="legal-section" data-testid="delete-method-email">
            <h2>
              <Mail size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {doc.emailTitle}
            </h2>
            <p>{doc.emailStepsHeader}</p>
            <ul className="legal-bulleted">
              {doc.emailStepsBody.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
            <PublicDeletionForm isRTL={isRTL} />
            <p className="legal-fineprint" style={{ marginTop: 12 }}>
              {isRTL ? 'إذا واجهت مشكلة، راسلنا على: ' : 'If you run into any issue, contact: '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Wassla account deletion request')}`} data-testid="delete-account-mailto">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </section>

          {/* Retention */}
          <section className="legal-section" data-testid="delete-retention">
            <h2>{doc.keepTitle}</h2>
            <ul className="legal-bulleted">
              {doc.keepList.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
            <p className="legal-fineprint">{doc.contact}</p>
          </section>
        </div>

        <div className="legal-footer-links" data-testid="delete-account-footer-links">
          <Link to="/privacy-policy" data-testid="delete-link-privacy">
            {isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Link>
          <span aria-hidden="true"> · </span>
          <Link to="/terms" data-testid="delete-link-terms">
            {isRTL ? 'شروط الاستخدام' : 'Terms of Use'}
          </Link>
        </div>
      </div>
    </div>
  );
}
